import { useEffect, useRef, useId } from "react";
import { motion, useReducedMotion, useInView, useMotionValue, animate } from "framer-motion";

/**
 * The unified "خطّ سياق" — a single copper line (Coral → Peach → Purple) that
 * draws itself once on enter and (optionally) sends a light dot along it.
 * Reused across Hero, Approach, Services, About, Impact story, Products & CTA.
 *
 * Fail-open + reduced-motion: the line + final state render immediately.
 */
export default function ContextLine({
  d,
  viewBox = "0 0 1200 120",
  withDot = false,
  duration = 2.6,
  strokeWidth = 2,
  className = "",
  once = true,
  onProgress,
  vertical = false, // affects gradient direction only
}) {
  const gid = useId().replace(/:/g, "");
  const reduced = useReducedMotion();
  const svgRef = useRef(null);
  const pathRef = useRef(null);
  const dotRef = useRef(null);
  const inView = useInView(svgRef, { once, amount: 0.4 });
  const progress = useMotionValue(0);

  useEffect(() => {
    if (!inView) return undefined;
    if (reduced) {
      onProgress?.(1);
      if (withDot && pathRef.current && dotRef.current) {
        const len = pathRef.current.getTotalLength();
        const pt = pathRef.current.getPointAtLength(len);
        dotRef.current.setAttribute("cx", pt.x);
        dotRef.current.setAttribute("cy", pt.y);
      }
      return undefined;
    }
    const controls = animate(progress, 1, { duration, ease: [0.4, 0, 0.2, 1] });
    const unsub = progress.on("change", (v) => {
      onProgress?.(v);
      if (withDot && pathRef.current && dotRef.current) {
        const len = pathRef.current.getTotalLength();
        const pt = pathRef.current.getPointAtLength(len * v);
        dotRef.current.setAttribute("cx", pt.x);
        dotRef.current.setAttribute("cy", pt.y);
      }
    });
    return () => { controls.stop(); unsub(); };
  }, [inView, reduced, withDot, duration, onProgress, progress]);

  return (
    <svg ref={svgRef} className={className} viewBox={viewBox} fill="none" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`cl-${gid}`} x1="0%" y1="0%" x2={vertical ? "0%" : "100%"} y2={vertical ? "100%" : "0%"}>
          <stop offset="0%" stopColor="#6E5A7B" />
          <stop offset="50%" stopColor="#E56B6F" />
          <stop offset="100%" stopColor="#EAAD8C" />
        </linearGradient>
      </defs>
      <motion.path
        ref={pathRef}
        d={d}
        stroke={`url(#cl-${gid})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={reduced ? false : { pathLength: 0 }}
        animate={inView && !reduced ? { pathLength: 1 } : reduced ? { pathLength: 1 } : { pathLength: 0 }}
        transition={{ duration, ease: [0.4, 0, 0.2, 1] }}
      />
      {withDot && (
        <circle ref={dotRef} r={5.5} fill="#E56B6F">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="1.6s" repeatCount="indefinite" />
        </circle>
      )}
    </svg>
  );
}
